import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    PlayCircle, PauseCircle, Repeat, Sparkles, ArrowDown, Rocket,
    Download, ScanText, Tags, GitCompare, Recycle, ClipboardList, FileStack,
    LayoutDashboard, Coins, Clock, TrendingUp, Maximize2, Minimize2, ChevronRight,
    Volume2, VolumeX, CheckCircle2, Library, Video, StopCircle,
    Languages, Mic, Package,
} from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const STAGE_ICON = {
    1: Download, 2: Recycle, 3: Tags, 4: GitCompare, 5: Clock,
    6: ClipboardList, 7: ClipboardList, 8: FileStack, 9: FileStack,
    10: LayoutDashboard, 11: TrendingUp, 12: Coins, 13: Rocket,
};

const AUTOPLAY_MS = 3800;

export default function ExecutiveDemo() {
    const [scenarios, setScenarios] = useState([]);
    const [searchParams] = useSearchParams();
    const [scenarioKey, setScenarioKey] = useState(searchParams.get("scenario") || "gold_plant");
    const [demo, setDemo] = useState(null);
    const [visible, setVisible] = useState(0);
    const [autoplay, setAutoplay] = useState(false);
    const [narration, setNarration] = useState(true);
    const [presentation, setPresentation] = useState(false);
    const [running, setRunning] = useState(false);

    const rootRef = useRef(null);
    const speechRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const [recording, setRecording] = useState(false);

    // Load scenarios
    useEffect(() => {
        api.get("/edi/demo/scenarios")
            .then((r) => setScenarios(r.data.scenarios || []))
            .catch(() => toast.error("Failed to load demonstration library"));
    }, []);

    const activeScenario = useMemo(
        () => scenarios.find((s) => s.key === scenarioKey) || scenarios[0] || null,
        [scenarios, scenarioKey],
    );

    // Speak narration when a new stage becomes visible
    useEffect(() => {
        if (!demo || !narration || visible === 0) return;
        const step = demo.steps.find((s) => s.idx === visible);
        if (!step || typeof window === "undefined" || !("speechSynthesis" in window)) return;
        try {
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(step.narration);
            u.rate = 1.02; u.pitch = 1.0; u.volume = 0.95;
            speechRef.current = u;
            window.speechSynthesis.speak(u);
        } catch { /* silent */ }
    }, [visible, demo, narration]);

    // Autoplay stage advance
    useEffect(() => {
        if (!autoplay || !demo || !running) return;
        if (visible >= demo.steps.length) { setAutoplay(false); setRunning(false); return; }
        const t = setTimeout(() => setVisible((v) => v + 1), AUTOPLAY_MS);
        return () => clearTimeout(t);
    }, [autoplay, running, visible, demo]);

    const stop = () => {
        try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
        setAutoplay(false); setRunning(false);
    };

    const run = async (kind = "step") => {
        stop();
        setVisible(0);
        try {
            const { data } = await api.post("/edi/demo/run", { scenario_key: scenarioKey });
            setDemo(data);
            if (kind === "autoplay") {
                setAutoplay(true); setRunning(true);
                setTimeout(() => setVisible(1), 60);
            } else {
                // staged reveal, no auto-progression after
                for (let i = 1; i <= data.steps.length; i++) {
                    await new Promise((r) => setTimeout(r, 260));
                    setVisible(i);
                }
                toast.success("Demonstration complete");
            }
        } catch {
            toast.error("Demonstration failed");
        }
    };

    const advance = () => {
        if (!demo) return;
        if (visible >= demo.steps.length) return;
        setVisible((v) => v + 1);
    };

    const reset = () => {
        stop(); setVisible(0); setDemo(null);
    };

    // Enter/exit fullscreen for presentation mode
    const togglePresentation = async () => {
        const next = !presentation;
        setPresentation(next);
        try {
            if (next) {
                await rootRef.current?.requestFullscreen?.();
            } else if (document.fullscreenElement) {
                await document.exitFullscreen();
            }
        } catch { /* browser may reject */ }
    };

    // Executive Rehearsal Recording — captures the tab + microphone as .webm
    const startRecording = async () => {
        if (recording) return;
        if (!navigator.mediaDevices?.getDisplayMedia) {
            toast.error("Screen recording is not supported in this browser");
            return;
        }
        try {
            const display = await navigator.mediaDevices.getDisplayMedia({
                video: { frameRate: 30 },
                audio: true,
            });
            let combined = display;
            try {
                const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
                combined = new MediaStream([
                    ...display.getVideoTracks(),
                    ...display.getAudioTracks(),
                    ...mic.getAudioTracks(),
                ]);
            } catch { /* microphone optional */ }
            const rec = new MediaRecorder(combined, { mimeType: "video/webm;codecs=vp9,opus" });
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: "video/webm" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
                a.href = url;
                a.download = `SGSBateman_Executive_Rehearsal_${scenarioKey}_${stamp}.webm`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 5000);
                combined.getTracks().forEach((t) => t.stop());
                toast.success("Rehearsal recording downloaded");
            };
            // stop when the user ends the shared surface from the browser UI
            display.getVideoTracks()[0].addEventListener("ended", () => {
                if (rec.state !== "inactive") rec.stop();
                setRecording(false);
            });
            rec.start(1000);
            recorderRef.current = rec;
            setRecording(true);
            toast.success("Recording — pick 'This tab' when prompted, then walk through the rehearsal");
        } catch (e) {
            toast.error("Recording cancelled");
        }
    };

    const stopRecording = () => {
        try {
            if (recorderRef.current && recorderRef.current.state !== "inactive") {
                recorderRef.current.stop();
            }
        } catch { /* noop */ }
        setRecording(false);
    };

    // Executive Recording Studio — Capture Once, Use Many Times™.
    // Same presentation model, four executive outputs (MP4 · PDF · PPTX · DOCX).
    const [exporting, setExporting] = useState(null);
    const [voices, setVoices] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [voice, setVoice] = useState("onyx");
    const [language, setLanguage] = useState("en");
    const [audioTrack, setAudioTrack] = useState(true);

    useEffect(() => {
        api.get("/edi/narration/voices").then((r) => setVoices(r.data.voices || [])).catch(() => {});
        api.get("/edi/narration/languages").then((r) => setLanguages(r.data.languages || [])).catch(() => {});
    }, []);

    const _download = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
    };

    const exportRehearsal = async (fmt) => {
        if (exporting) return;
        setExporting(fmt);
        const label = fmt.toUpperCase();
        try {
            toast.info(fmt === "mp4"
                ? `Rendering executive video · ${voice.toUpperCase()} · ${language.toUpperCase()}`
                : `Composing Executive Rehearsal · ${label}`);
            const token = localStorage.getItem("sgsb.token");
            const path = fmt === "mp4" ? "/edi/rehearsal/export/multimedia" : "/edi/rehearsal/export";
            const payload = fmt === "mp4"
                ? { scenario_key: scenarioKey, fmt, language, voice, include_audio: audioTrack,
                    include_subtitles: true, include_transcript: true, include_chapters: true }
                : { scenario_key: scenarioKey, fmt };
            const resp = await fetch(`${api.defaults.baseURL}${path}`, {
                method: "POST",
                headers: { "Content-Type": "application/json",
                            Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            if (!resp.ok) throw new Error(String(resp.status));
            const cd = resp.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : `SGSBateman_Rehearsal.${fmt}`;
            _download(await resp.blob(), filename);
            toast.success(`Executive Rehearsal · ${label} downloaded`);
        } catch (e) {
            toast.error(`Executive Rehearsal · ${label} export failed`);
        } finally {
            setExporting(null);
        }
    };

    // Executive Demonstration Package — 7-in-1 ZIP bundle.
    const [packaging, setPackaging] = useState(false);
    const exportPackage = async () => {
        if (packaging) return;
        setPackaging(true);
        try {
            toast.info(`Composing Executive Demonstration Package · ${voice.toUpperCase()} · ${language.toUpperCase()}`);
            const token = localStorage.getItem("sgsb.token");
            const resp = await fetch(`${api.defaults.baseURL}/edi/demo/package`, {
                method: "POST",
                headers: { "Content-Type": "application/json",
                            Authorization: `Bearer ${token}` },
                body: JSON.stringify({ scenario_key: scenarioKey, language, voice, include_audio: audioTrack }),
            });
            if (!resp.ok) throw new Error(String(resp.status));
            const cd = resp.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : "SGSBateman_Executive_Package.zip";
            _download(await resp.blob(), filename);
            toast.success("Executive Demonstration Package downloaded");
        } catch {
            toast.error("Executive Demonstration Package failed");
        } finally {
            setPackaging(false);
        }
    };
    useEffect(() => {
        const onFs = () => setPresentation(!!document.fullscreenElement);
        document.addEventListener("fullscreenchange", onFs);
        return () => {
            document.removeEventListener("fullscreenchange", onFs);
            try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
        };
    }, []);

    const activeStep = demo?.steps.find((s) => s.idx === visible);

    return (
        <div
            ref={rootRef}
            data-testid={EDI.executiveDemoRoot}
            className={`space-y-8 ${presentation ? "bg-slate-950 min-h-screen p-8" : ""}`}
        >
            {/* Hero + scenario library */}
            <div className="relative overflow-hidden rounded-2xl glass p-8">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.24), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(59,130,246,0.20), transparent 55%)",
                    }}
                />
                <div className="relative">
                    <div className="flex items-center gap-2">
                        <Library className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <div className="kicker text-[var(--sgsb-gold)]">Executive Rehearsal Mode™ · Demonstration Library</div>
                    </div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter leading-[0.98]">
                        {demo ? demo.title : (activeScenario?.title || "From Historical Knowledge to a New Gold Plant — in Minutes.")}
                    </h1>
                    <p className="mt-4 max-w-3xl text-slate-300 font-body text-base leading-relaxed">
                        A guided, narrated executive demonstration of the full lifecycle — from
                        historical corporate archive to a board-ready bid pack. Choose a scenario,
                        press <span className="text-[var(--sgsb-gold)]">Rehearse</span>,
                        and step through — or auto-play for a full boardroom rehearsal.
                    </p>

                    {/* Scenario chips */}
                    <div className="mt-6 flex flex-wrap gap-2">
                        {scenarios.map((s) => (
                            <button
                                key={s.key}
                                data-testid={EDI.executiveDemoScenario(s.key)}
                                onClick={() => { setScenarioKey(s.key); setDemo(null); setVisible(0); stop(); }}
                                className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-display font-bold border transition-colors duration-200 ${
                                    scenarioKey === s.key
                                        ? "bg-[var(--sgsb-gold)] text-slate-950 border-[var(--sgsb-gold)]"
                                        : "border-slate-800 text-slate-300 hover:text-[var(--sgsb-gold)] hover:border-[color:var(--sgsb-gold)]/50"
                                }`}
                            >
                                {s.commodity} · {s.process}
                                <span className="text-[10px] opacity-70">{s.similarity}%</span>
                            </button>
                        ))}
                    </div>

                    {/* Controls */}
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <button
                            data-testid={EDI.executiveDemoTrigger}
                            onClick={() => run("step")}
                            disabled={running}
                            className="inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-5 py-3 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                        >
                            <PlayCircle className="h-4 w-4" strokeWidth={2} />
                            {demo ? "Rehearse again" : "Rehearse executive demonstration"}
                        </button>
                        <button
                            data-testid={EDI.executiveDemoAutoplay}
                            onClick={() => run("autoplay")}
                            disabled={running && autoplay}
                            className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/60 text-[var(--sgsb-gold)] px-4 py-2 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold)]/10 disabled:opacity-40 transition-colors duration-200"
                        >
                            {autoplay ? <PauseCircle className="h-3.5 w-3.5" strokeWidth={2} /> : <PlayCircle className="h-3.5 w-3.5" strokeWidth={2} />}
                            {autoplay ? "Pause auto-play" : "Auto-play"}
                        </button>
                        <button
                            data-testid={EDI.executiveDemoPresentation}
                            onClick={togglePresentation}
                            className="inline-flex items-center gap-2 rounded-md border border-slate-700 text-slate-200 px-4 py-2 font-display font-bold text-xs hover:border-[color:var(--sgsb-gold)]/50 hover:text-[var(--sgsb-gold)] transition-colors duration-200"
                        >
                            {presentation ? <Minimize2 className="h-3.5 w-3.5" strokeWidth={2} /> : <Maximize2 className="h-3.5 w-3.5" strokeWidth={2} />}
                            {presentation ? "Exit Presentation Mode" : "Presentation Mode"}
                        </button>
                        <button
                            data-testid={EDI.executiveDemoNarration}
                            onClick={() => { setNarration((n) => !n); if (narration) try { window.speechSynthesis?.cancel(); } catch { /* noop */ } }}
                            className="inline-flex items-center gap-2 rounded-md border border-slate-700 text-slate-200 px-4 py-2 font-display font-bold text-xs hover:border-[color:var(--sgsb-gold)]/50 hover:text-[var(--sgsb-gold)] transition-colors duration-200"
                        >
                            {narration ? <Volume2 className="h-3.5 w-3.5" strokeWidth={2} /> : <VolumeX className="h-3.5 w-3.5" strokeWidth={2} />}
                            {narration ? "Narration on" : "Narration off"}
                        </button>
                        <button
                            data-testid={EDI.executiveDemoReset}
                            onClick={reset}
                            disabled={!demo}
                            className="inline-flex items-center gap-2 rounded-md border border-slate-700 text-slate-300 px-4 py-2 font-display font-bold text-xs hover:border-[color:var(--sgsb-gold)]/50 hover:text-[var(--sgsb-gold)] disabled:opacity-40 transition-colors duration-200"
                        >
                            <Repeat className="h-3.5 w-3.5" strokeWidth={2} />
                            Reset
                        </button>
                        {!recording ? (
                            <button
                                data-testid={EDI.executiveDemoRecord}
                                onClick={startRecording}
                                className="inline-flex items-center gap-2 rounded-md border border-red-500/50 text-red-400 px-4 py-2 font-display font-bold text-xs hover:bg-red-500/10 transition-colors duration-200"
                            >
                                <Video className="h-3.5 w-3.5" strokeWidth={2} />
                                Quick capture (WebM)
                            </button>
                        ) : (
                            <button
                                data-testid={EDI.executiveDemoRecordStop}
                                onClick={stopRecording}
                                className="inline-flex items-center gap-2 rounded-md bg-red-500 text-white px-4 py-2 font-display font-bold text-xs hover:bg-red-400 transition-colors duration-200"
                            >
                                <StopCircle className="h-3.5 w-3.5" strokeWidth={2} />
                                Stop &amp; download
                            </button>
                        )}
                    </div>

                    {/* Recording Studio — Capture Once, Use Many Times™ */}
                    <div className="mt-8 rounded-xl border border-slate-800/70 bg-slate-950/40 p-5">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <FileStack className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                            <span className="kicker text-[var(--sgsb-gold)]">Executive Recording Studio</span>
                            <span className="text-[10px] kicker text-slate-500 ml-2">
                                One presentation · four executive outputs · Capture Once, Use Many Times™
                            </span>
                        </div>

                        {/* Multimedia framework — language · voice · audio · package */}
                        <div className="mb-4 rounded-md border border-[color:var(--sgsb-gold)]/25 bg-slate-950/50 p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Languages className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <span className="kicker text-[10px] text-[var(--sgsb-gold)]">Executive Multimedia Framework</span>
                                <span className="text-[10px] kicker text-slate-500 ml-2">
                                    THOM360 Enterprise Services · shared across every 360 sector platform
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                                <label className="block">
                                    <span className="kicker text-[10px] text-slate-500">Narration language</span>
                                    <select
                                        data-testid={EDI.executiveDemoLanguage}
                                        value={language}
                                        onChange={(e) => setLanguage(e.target.value)}
                                        className="mt-1 w-full rounded-md bg-slate-900 border border-slate-800 text-slate-200 text-xs px-3 py-2 font-body focus:border-[color:var(--sgsb-gold)]/60 focus:outline-none"
                                    >
                                        {languages.map((l) => (
                                            <option key={l.code} value={l.code}>{l.label}{l.region ? ` · ${l.region}` : ""}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="kicker text-[10px] text-slate-500">Executive voice</span>
                                    <select
                                        data-testid={EDI.executiveDemoVoice}
                                        value={voice}
                                        onChange={(e) => setVoice(e.target.value)}
                                        className="mt-1 w-full rounded-md bg-slate-900 border border-slate-800 text-slate-200 text-xs px-3 py-2 font-body focus:border-[color:var(--sgsb-gold)]/60 focus:outline-none"
                                    >
                                        {voices.map((v) => (
                                            <option key={v.code} value={v.code}>{v.label}{v.default ? " · default" : ""}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="flex items-center gap-2 mt-5">
                                    <input
                                        type="checkbox"
                                        data-testid={EDI.executiveDemoAudioToggle}
                                        checked={audioTrack}
                                        onChange={(e) => setAudioTrack(e.target.checked)}
                                        className="h-4 w-4 accent-[color:var(--sgsb-gold)]"
                                    />
                                    <span className="text-xs text-slate-300 font-body">
                                        <Mic className="inline h-3 w-3 mr-1 text-[var(--sgsb-gold)]" strokeWidth={2} />
                                        Include narration audio + subtitles
                                    </span>
                                </label>
                                <button
                                    data-testid={EDI.executiveDemoPackage}
                                    onClick={exportPackage}
                                    disabled={packaging}
                                    className="inline-flex items-center justify-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-3 py-2 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-40 transition-colors duration-200"
                                >
                                    <Package className="h-3.5 w-3.5" strokeWidth={2} />
                                    {packaging ? "Composing…" : "Executive Package (ZIP)"}
                                </button>
                            </div>
                            <div className="mt-2 text-[10px] text-slate-500 kicker">
                                Package includes: MP4 recording · board brief PDF · executive summary · presentation PPTX · verification cert PDF · audit report · SRT · transcript · chapters JSON
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {/* Production video */}
                            <div className="rounded-md border border-slate-800 bg-slate-950/60 p-4 flex flex-col">
                                <div className="kicker text-[10px] text-[var(--sgsb-gold)]">Production Mode</div>
                                <div className="mt-1 font-display font-black text-white text-sm tracking-tight">Narrated MP4</div>
                                <div className="mt-1 text-[11px] text-slate-400 font-body">
                                    Server-rendered enterprise video. Chapter markers, SGS Bateman branding,
                                    board-ready portability. Suitable for client distribution and executive playback.
                                </div>
                                <button
                                    data-testid={EDI.executiveDemoRecordServer}
                                    onClick={() => exportRehearsal("mp4")}
                                    disabled={exporting}
                                    className="mt-3 inline-flex items-center justify-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-3 py-2 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-40 transition-colors duration-200"
                                >
                                    <Video className="h-3.5 w-3.5" strokeWidth={2} />
                                    {exporting === "mp4" ? "Rendering…" : "Render executive video"}
                                </button>
                            </div>
                            {/* Quick capture */}
                            <div className="rounded-md border border-slate-800 bg-slate-950/60 p-4 flex flex-col">
                                <div className="kicker text-[10px] text-[var(--sgsb-gold)]">Quick Capture Mode</div>
                                <div className="mt-1 font-display font-black text-white text-sm tracking-tight">Browser WebM</div>
                                <div className="mt-1 text-[11px] text-slate-400 font-body">
                                    Instant tab capture from your browser. Suitable for rapid internal
                                    demonstrations, side-by-side comparisons and rehearsal QA.
                                </div>
                                <div className="mt-3 text-[11px] text-slate-500 font-body">
                                    Use the Quick capture button above — it&apos;ll prompt for tab + mic.
                                </div>
                            </div>
                            {/* Executive Export */}
                            <div className="rounded-md border border-slate-800 bg-slate-950/60 p-4 flex flex-col">
                                <div className="kicker text-[10px] text-[var(--sgsb-gold)]">Executive Export Mode</div>
                                <div className="mt-1 font-display font-black text-white text-sm tracking-tight">Board Brief · PDF · PPTX · Word</div>
                                <div className="mt-1 text-[11px] text-slate-400 font-body">
                                    Same rehearsal storyline, narrated and rendered as executive documents.
                                    Chapter-per-stage. SGS Bateman cover, footer, confidentiality.
                                </div>
                                <div className="mt-3 grid grid-cols-3 gap-2">
                                    {[["pdf","PDF"],["pptx","PPTX"],["docx","Word"]].map(([fmt, label]) => (
                                        <button
                                            key={fmt}
                                            data-testid={EDI.executiveDemoExport(fmt)}
                                            onClick={() => exportRehearsal(fmt)}
                                            disabled={exporting}
                                            className="inline-flex items-center justify-center gap-1 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-2 py-1.5 font-display font-bold text-[11px] hover:bg-[color:var(--sgsb-gold)]/10 disabled:opacity-40 transition-colors duration-200"
                                        >
                                            <FileStack className="h-3 w-3" strokeWidth={2} />
                                            {exporting === fmt ? "…" : label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Active narration panel */}
            {demo && activeStep && (
                <div className="glass rounded-2xl p-6 relative overflow-hidden">
                    <div
                        className="absolute inset-0 opacity-45 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(ellipse at 90% 20%, rgba(212,175,55,0.20), transparent 55%), radial-gradient(ellipse at 10% 80%, rgba(16,185,129,0.14), transparent 55%)",
                        }}
                    />
                    <div className="relative flex items-start gap-5">
                        <div className="h-14 w-14 rounded-md border border-[color:var(--sgsb-gold)]/40 bg-[color:var(--sgsb-gold)]/10 grid place-items-center shrink-0">
                            <Sparkles className="h-6 w-6 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="kicker text-slate-500 text-[10px]">
                                Stage {activeStep.idx} of {demo.steps.length} · {demo.commodity} · {demo.reference}
                            </div>
                            <h2 className="mt-1 font-display font-black text-white text-2xl md:text-3xl tracking-tight">
                                {activeStep.stage}
                            </h2>
                            <p className="mt-3 text-slate-200 font-body text-base leading-relaxed max-w-4xl">
                                {activeStep.narration}
                            </p>
                            <div className="mt-3 text-[13px] text-[var(--sgsb-gold)] font-display font-bold">
                                {activeStep.artifact}
                            </div>
                        </div>
                        <button
                            data-testid={EDI.executiveDemoNext}
                            onClick={advance}
                            disabled={autoplay || visible >= demo.steps.length}
                            className="inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-4 py-2 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-40 transition-colors duration-200 shrink-0"
                        >
                            Next stage
                            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                    </div>
                </div>
            )}

            {/* Stage list */}
            {demo && (
                <div className="glass rounded-xl p-6">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <span className="kicker text-slate-400">Guided lifecycle · {demo.steps.length} stages</span>
                        <div className="flex-1 h-px bg-slate-800/70" />
                        <span className="kicker text-[10px] text-[var(--sgsb-gold)]">{Math.min(visible, demo.steps.length)}/{demo.steps.length}</span>
                    </div>

                    <div className="mt-6 space-y-3">
                        {demo.steps.map((s) => {
                            const shown = s.idx <= visible;
                            const isActive = s.idx === visible;
                            const Icon = STAGE_ICON[s.idx] || Sparkles;
                            return (
                                <div
                                    key={s.idx}
                                    data-testid={EDI.executiveDemoStage(s.idx)}
                                    className="rounded-md border p-4 flex items-start gap-3 relative"
                                    style={{
                                        borderColor: isActive ? "rgba(212,175,55,0.75)" : shown ? "rgba(212,175,55,0.45)" : "rgba(148,163,184,0.14)",
                                        background: isActive ? "rgba(212,175,55,0.10)" : shown ? "rgba(212,175,55,0.06)" : "rgba(2,6,23,0.55)",
                                        opacity: shown ? 1 : 0.4,
                                        transform: shown ? "translateY(0)" : "translateY(4px)",
                                        transition: "opacity 320ms ease, transform 320ms ease, background-color 320ms ease, border-color 320ms ease",
                                    }}
                                >
                                    <div className="h-9 w-9 rounded-md border border-[color:var(--sgsb-gold)]/40 bg-[color:var(--sgsb-gold)]/10 grid place-items-center shrink-0">
                                        <Icon className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="kicker text-slate-500 text-[10px]">Stage {s.idx}</div>
                                            <div className="font-display font-black text-white text-[15px] tracking-tight">{s.stage}</div>
                                            {shown && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" strokeWidth={2} />}
                                        </div>
                                        <div className="mt-1 text-sm text-slate-300 font-body">{s.narration}</div>
                                        <div className="mt-1 text-[11px] text-[var(--sgsb-gold)] font-display font-bold">{s.artifact}</div>
                                    </div>
                                    {s.idx < demo.steps.length && (
                                        <ArrowDown className="hidden md:block absolute -bottom-3 left-6 h-4 w-4 text-slate-700 z-10" strokeWidth={1.5} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Outcome */}
            {demo && visible >= demo.steps.length && (
                <div className="glass rounded-xl p-6 relative overflow-hidden">
                    <div
                        className="absolute inset-0 opacity-40 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(ellipse at 90% 10%, rgba(16,185,129,0.20), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(212,175,55,0.16), transparent 55%)",
                        }}
                    />
                    <div className="relative">
                        <div className="kicker text-[var(--sgsb-gold)]">Executive outcome</div>
                        <h3 className="mt-1 font-display font-black text-white text-2xl tracking-tighter">
                            One opportunity — measurable enterprise leverage.
                        </h3>
                        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-4">
                            {[
                                ["Hours Saved",             demo.outcome.hours_saved.toLocaleString()],
                                ["Cost Saved",              demo.outcome.cost_saved],
                                ["Delivery Acceleration",   `${demo.outcome.delivery_acceleration_wks} wks earlier`],
                                ["Best Match Similarity",   `${demo.outcome.similarity_pct}%`],
                                ["Baseline Confidence",     `${demo.outcome.confidence_pct}%`],
                            ].map(([k, v]) => (
                                <div key={k} className="rounded-md bg-slate-950/40 border border-slate-800 p-4">
                                    <div className="kicker text-slate-500 text-[10px]">{k}</div>
                                    <div className="mt-1 font-display font-black text-white text-xl tracking-tight">{v}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-5 text-sm text-slate-300 font-body">
                            <Rocket className="inline h-3.5 w-3.5 mr-2 text-[var(--sgsb-gold)]" strokeWidth={2} />
                            One archive · one rehearsal · one board-ready plant baseline.
                            This is Enterprise Delivery Intelligence™.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
